import type { ActivationFormat, ActivationStatus } from "@/hooks/use-engage-campaigns";

export type FamilyKey = "product" | "events" | "content" | "affiliation";

export interface FamilyStage {
  status: ActivationStatus;
  label: string;
  hint: string;
}

export interface FamilyDef {
  key: FamilyKey;
  label: string;
  /** One line shown under the workspace title. */
  tagline: string;
  /** What the brand actually gets, in plain words, for someone choosing a type for the first time. */
  pickerHint: string;
  /** What a brand manager is trying to get done here. */
  jobToBeDone: string;
  formats: ActivationFormat[];
  defaultFormat: ActivationFormat;
  /** Same 5 DB statuses for every family, relabelled to the family's own vocabulary. */
  stages: FamilyStage[];
  /** Wording for the unit being delivered ("event", "drop", "piece"...). */
  unit: { singular: string; plural: string };
  /** First-run empty state, shown before the brand has briefed anything. */
  empty: { title: string; description: string; cta: string };
}

// Sidebar order. Content comes last: it is most often an add-on to another activation.
export const ALL_FAMILIES: FamilyKey[] = ["product", "events", "affiliation", "content"];

export const FAMILIES: Record<FamilyKey, FamilyDef> = {
  product: {
    key: "product",
    label: "Seeding",
    tagline: "Get your product into the right hands.",
    pickerHint: "Send your product to communities to try and review it.",
    jobToBeDone: "Seed, sample and test products with communities, and collect honest feedback.",
    formats: ["gifting", "testing", "panel"],
    defaultFormat: "gifting",
    unit: { singular: "drop", plural: "drops" },
    empty: { title: "Run your first seeding", description: "Tell us who should get your product. We find the communities and handle the distribution.", cta: "Brief ernest" },
    stages: [
      { status: "shortlisted", label: "Shortlist", hint: "Communities you want to seed" },
      { status: "coordination", label: "Terms & delivery", hint: "Agree quantity, address, timing" },
      { status: "confirmed", label: "Shipped", hint: "Product is on its way or handed over" },
      { status: "waiting-feedback", label: "Feedback due", hint: "Waiting for member feedback" },
      { status: "completed", label: "Closed", hint: "Feedback collected" },
    ],
  },
  events: {
    key: "events",
    label: "Events",
    tagline: "Join key moments or host your own.",
    pickerHint: "Sponsor a community event, or host your own.",
    jobToBeDone: "Sponsor an event, or launch your own and invite communities, and prove it was worth it.",
    formats: ["sponsorship", "community-sponsorship", "private-experience", "retreat-residency", "workshop", "co-created-event"],
    defaultFormat: "sponsorship",
    unit: { singular: "event", plural: "events" },
    empty: { title: "Plan your first event", description: "Be part of an existing event, or host your own and invite communities. We handle the rest.", cta: "Brief ernest" },
    stages: [
      { status: "shortlisted", label: "Shortlist", hint: "Communities you want to sponsor" },
      { status: "coordination", label: "Negotiating", hint: "Agree date, budget and what you get" },
      { status: "confirmed", label: "Locked in", hint: "Date and terms agreed" },
      { status: "waiting-feedback", label: "Debrief", hint: "Event is done, collect results" },
      { status: "completed", label: "Done", hint: "Results recorded" },
    ],
  },
  content: {
    key: "content",
    label: "Content",
    tagline: "Turn community moments into content.",
    pickerHint: "Communities create photos and videos about your brand.",
    jobToBeDone: "Commission UGC and branded content, approve it, and secure the rights to reuse it.",
    formats: ["ugc", "branded-content"],
    defaultFormat: "ugc",
    unit: { singular: "piece", plural: "pieces" },
    empty: { title: "Commission your first content", description: "Tell us the content you want and where it will live. We find who creates it and manage the approvals.", cta: "Brief ernest" },
    stages: [
      { status: "shortlisted", label: "Shortlist", hint: "Communities and creators you want" },
      { status: "coordination", label: "Brief & rights", hint: "Agree deliverables and usage rights" },
      { status: "confirmed", label: "In production", hint: "Content is being made" },
      { status: "waiting-feedback", label: "Review", hint: "Content delivered, awaiting your approval" },
      { status: "completed", label: "Published", hint: "Approved and live" },
    ],
  },
  affiliation: {
    key: "affiliation",
    label: "Affiliation",
    tagline: "Members promote you and get rewarded.",
    pickerHint: "Members recommend you and earn a reward for it.",
    jobToBeDone: "Run ambassador and affiliate programs with clear terms and trackable results.",
    formats: ["ambassador", "affiliate"],
    defaultFormat: "ambassador",
    unit: { singular: "program", plural: "programs" },
    empty: { title: "Launch your first ambassador program", description: "Tell us the ambassadors you want and how you reward them. We recruit them and run the program.", cta: "Brief ernest" },
    stages: [
      { status: "shortlisted", label: "Shortlist", hint: "Communities you want on board" },
      { status: "coordination", label: "Terms", hint: "Agree perks, commission and duration" },
      { status: "confirmed", label: "Active", hint: "Program is running" },
      { status: "waiting-feedback", label: "Review", hint: "Check performance and renew" },
      { status: "completed", label: "Ended", hint: "Program closed" },
    ],
  },
};

export const FAMILY_LIST: FamilyDef[] = ALL_FAMILIES.map((k) => FAMILIES[k]);

export function familyOfFormat(format: ActivationFormat | string | null | undefined): FamilyKey | null {
  for (const f of FAMILY_LIST) {
    if (f.formats.includes(format as ActivationFormat)) return f.key;
  }
  return null;
}

export function isFamilyKey(v: string | undefined): v is FamilyKey {
  return !!v && (ALL_FAMILIES as string[]).includes(v);
}

/** Where an activation's detail view lives: its family's page, or the generic file for unclassified formats. */
export function activationPath(format: ActivationFormat | string | null | undefined, id: string): string {
  const family = familyOfFormat(format);
  return family ? `/activate/${family}/${id}` : `/engage/activation/${id}/detail`;
}

export interface FamilyCount {
  family: FamilyKey;
  count: number;
}

/** How many live activations sit in each family, in sidebar order, skipping empty ones. */
export function familyMix(activations: { format: ActivationFormat | string; status: string }[]): FamilyCount[] {
  const live = activations.filter((a) => a.status !== "cancelled");
  return ALL_FAMILIES
    .map((family) => ({ family, count: live.filter((a) => familyOfFormat(a.format) === family).length }))
    .filter((f) => f.count > 0);
}
